import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import CommentField from "../../../components/comment-field/CommentField";
import MessageItem from "../../../components/message/MessageItem";
import SvgLoadingAnimation from "../../../components/loading/index";
import Modal from "../../../components/modal/Modal";
import DropZoneOverlay from "../../../components/drop-zone-overlay/DropZoneOverlay";
import UploadProgressBar from "../../../components/upload-progress/UploadProgressBar";
import { useUploadFailureDialog } from "../../../components/upload-progress/useUploadFailureDialog";
import { chatMessageToMessageData } from "../../../utils/messageAdapters";
import {
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
  useMarkChatMessagesAsReadMutation,
  useMarkChatFilesAsDownloadedMutation,
} from "../../../app/services/chatMessages";
import type { ChatThread } from "../../../app/services/chatThreads";
import {
  useMessengerSubscribe,
  useMessengerReconnect,
} from "../../../app/messenger/MessengerSocketContext";
import { useObservedHeight } from "../../../hooks/useObservedHeight";
import useElementRect from "../../../hooks/useElementRect";
import { useScrollDirectionLayoutSync } from "../../../hooks/useScrollDirectionLayoutSync";
import { useBatchedCall } from "../../../hooks/useBatchedCall";
import {
  useS3Upload,
  FileWithPath,
  ValidationError,
} from "../../../hooks/useS3Upload";
import {
  isProducerGroupPosition,
  isSpecialistPosition,
} from "../../../utils/memberPositionUtils";

/**
 * オファー非依存のシンプルな DM スレッド用メッセンジャー（右カラム）。
 * 既存の巨大な契約用 `Messenger`（DM.tsx 内）には手を入れず、メッセージ送受信の
 * グローバル部品（CommentField / MessageItem）だけを再利用する軽量版。
 * ステータス・契約・ワークフロー UI は持たない。
 * ファイル添付はオファー DM と同方式（S3 アップロード → file_keys 付き送信、
 * purpose=chat_message）で対応する。
 *
 * 入力欄（CommentField）の固定配置は通常 DM の `DmActionPanel` と同じ方式に揃える:
 * `position: fixed` のパネルを `document.body` へ Portal で出し、`scrollState` /
 * BottomAppBar / SPECIALIST 判定に連動させる。TwoColumnLayout の transform 祖先の
 * 影響で内側 fixed が viewport 下端に届かず「浮く」問題を回避するため。
 */

interface ChatMessengerProps {
  thread: ChatThread;
  currentUserId?: number;
}

// 通常 DM（Messenger）と揃えた固定パネルの寸法。
// BottomAppBar 表示時に CommentField を持ち上げる量と、メッセージ最下部が
// パネルに隠れないよう確保する padding-bottom の下限・バッファ。
const BOTTOM_BAR_HEIGHT = 80;
const MESSENGER_BOTTOM_PADDING_BUFFER = 24;
const MESSENGER_BOTTOM_PADDING_MIN = 120;

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessageListScroll = styled.div<{ $bottomPadding: number }>`
  flex: 1;
  /* DropZoneOverlay（align-items: center の flex column）内でも全幅を保つ */
  width: 100%;
  overflow-y: auto;
  /* メッセージ量やカラム高さの変化でスクロールバーが出入りしても幅が変わらないように */
  scrollbar-gutter: stable;
  min-height: 0;
  padding: 16px;
  padding-bottom: ${({ $bottomPadding }) => $bottomPadding}px;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div.attrs({
  className: "bodytext-13 u-text-light-gray",
})`
  margin: auto;
  text-align: center;
`;

// 通常 DM の DmActionPanel と同じく document.body に Portal で固定配置する。
const ChatActionPanel = styled.div<{
  $bottom: number;
  $left: number;
  $width: number;
}>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 4;
  padding: 4px 16px;
  display: flex;
  justify-content: center;
  transition: bottom 0.15s linear;
`;

// アップロードモーダルのタイトル（通常 DM と同じ見た目）
const UploadModalTitle = styled.h3.attrs({
  className: "heading-13",
})`
  margin: 0;
  text-align: center;
  color: var(--soremo-text);
`;

const ChatMessenger: React.FC<ChatMessengerProps> = ({
  thread,
  currentUserId,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, refetch } = useGetChatMessagesQuery(
    { project_id: projectId!, chat_id: thread.id, limit: 100 },
    { skip: !projectId },
  );
  const [sendChatMessage] = useSendChatMessageMutation();
  const [markChatMessagesAsRead] = useMarkChatMessagesAsReadMutation();
  const [markChatFilesAsDownloaded] = useMarkChatFilesAsDownloadedMutation();

  // ─── ファイル添付（オファー DM と同方式: S3 → file_keys） ───
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesWithPath, setSelectedFilesWithPath] = useState<
    FileWithPath[]
  >([]);

  const {
    uploadFiles,
    uploadingFiles,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    withPostProcess,
  } =
    useS3Upload({
      purpose: "chat_message",
      projectId,
      onValidationError: (errors: ValidationError[]) => {
        errors.forEach((error) => {
          console.warn("[ChatMessenger] file validation error", error.message);
        });
      },
    });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // スレッド切替時に選択中ファイルをリセットする。
  useEffect(() => {
    setSelectedFiles([]);
    setSelectedFilesWithPath([]);
  }, [thread.id]);

  // アップロード完了後は進捗モーダルを自動で閉じる（通常 DM と同じ挙動）。
  // エラー行が残っている場合は自動クリアしない（再試行ボタン付きで表示し続ける）
  useEffect(() => {
    if (uploadingFiles.length === 0) return;
    const allCompleted = uploadingFiles.every((f) => f.status === "completed");
    if (allCompleted) {
      const timer = setTimeout(() => {
        clearCompleted();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  const handleFilesSelected = useCallback((files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  const handleFolderDrop = useCallback((filesWithPath: FileWithPath[]) => {
    if (filesWithPath.length === 0) return;
    setSelectedFilesWithPath((prev) => [...prev, ...filesWithPath]);
  }, []);

  // CommentField には通常選択 + フォルダ選択を結合して渡すため、削除は
  // 結合後 index から通常/フォルダのどちらかを判定する（通常 DM と同じ方式）。
  const handleRemoveFile = useCallback(
    (index: number) => {
      const normalFilesCount = selectedFiles.length;
      if (index < normalFilesCount) {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      } else {
        const folderIndex = index - normalFilesCount;
        setSelectedFilesWithPath((prev) =>
          prev.filter((_, i) => i !== folderIndex),
        );
      }
    },
    [selectedFiles.length],
  );

  const allSelectedFiles = useMemo(
    () => [...selectedFiles, ...selectedFilesWithPath.map((f) => f.file)],
    [selectedFiles, selectedFilesWithPath],
  );

  /**
   * 現在選択中のファイル（通常選択 + フォルダ選択）を S3 にアップロードし、
   * 続行してよい file_key を返す。失敗があれば「再試行 / 失敗分を除いて続行 /
   * キャンセル」ダイアログで解決し、キャンセル時は選択状態を復元して null を返す。
   */
  const uploadPendingFiles = useCallback(async (): Promise<string[] | null> => {
    if (selectedFiles.length === 0 && selectedFilesWithPath.length === 0)
      return [];
    const normalFilesWithPath: FileWithPath[] = selectedFiles.map((file) => ({
      file,
      relativePath: file.name,
    }));
    const allFilesToUpload: FileWithPath[] = [
      ...normalFilesWithPath,
      ...selectedFilesWithPath,
    ];
    setSelectedFiles([]);
    setSelectedFilesWithPath([]);

    const uploadResults = await uploadFiles(allFilesToUpload);
    const hadFailures = uploadResults.some((f) => f.status === "error");
    const resolved = await resolveUploadFailures(uploadResults);
    if (resolved === null) {
      // キャンセル: 進捗を畳み、添付を選択状態へ戻して中断
      clearCompleted();
      setSelectedFiles(selectedFiles);
      setSelectedFilesWithPath(selectedFilesWithPath);
      return null;
    }
    if (hadFailures) clearCompleted();
    return resolved
      .filter((f) => f.fileKey)
      .map((f) => f.fileKey as string);
  }, [selectedFiles, selectedFilesWithPath, uploadFiles, resolveUploadFailures, clearCompleted]);

  // API は新しい順（-created）で返すため、表示用に古い→新しいへ反転する。
  const messages = useMemo(() => {
    const list = data?.data?.messages ?? [];
    return [...list].reverse();
  }, [data]);

  // 自分のポジション（thread.members から取得）。membership API を別途叩かず、
  // スレッドの参加者情報から解決する。
  const myPosition = useMemo(
    () => thread.members.find((m) => m.id === currentUserId)?.position ?? "",
    [thread.members, currentUserId],
  );
  // AI 支援の出し分けはポジションで固定する（直近メッセージの送信者では切り替えない）。
  // - プロデューサーグループ（PRODUCER / DIRECTOR / ADMIN）: 返信君
  //   （コミュニケーションのハブとして相手に返信する立場）
  // - それ以外（CLIENT / REVIEWER / SPECIALIST）: チクチクフィルター（自分の文の校正）
  // これにより、クライアント × プロデューサーでは CLIENT=チクチク / PRODUCER=返信君、
  // プロデューサー × スペシャリストでは PRODUCER=返信君 / SPECIALIST=チクチク になる。
  const isProducerSide = isProducerGroupPosition(myPosition);
  // SPECIALIST は BottomAppBar 非表示のため、固定パネルを常に viewport 下端へ置く。
  const isSpecialist = isSpecialistPosition(myPosition);

  // CommentField 固定パネルの実高さ・横位置・スクロール連動（通常 DM と同じ機構）。
  const { ref: commentFieldRef, height: commentFieldHeight } =
    useObservedHeight<HTMLDivElement>();
  const panelRect = useElementRect(containerRef);
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  const {
    onWheel: handleWheel,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
  } = useScrollDirectionLayoutSync();

  // GlobalAudioToggle と同方式: BottomAppBar 表示時は 80px 上げる。
  // SPECIALIST は BAB が無いため常に 0（スクロール非連動で下端固定）。
  const actionPanelBottom = isSpecialist
    ? 0
    : scrollState !== "down"
      ? BOTTOM_BAR_HEIGHT
      : 0;
  // メッセージ最下部が固定パネルに隠れないよう padding-bottom を確保する。
  const messengerBottomPadding = Math.max(
    MESSENGER_BOTTOM_PADDING_MIN,
    commentFieldHeight + actionPanelBottom + MESSENGER_BOTTOM_PADDING_BUFFER,
  );

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages.length, thread.id]);

  // 自分宛で未読のメッセージを既読化する。
  useEffect(() => {
    if (!projectId) return;
    const unreadIds = messages
      .filter((m) => m.author?.id !== currentUserId)
      .filter((m) => {
        const mine = m.receivers.find((r) => r.id === currentUserId);
        return mine && !mine.seen_at;
      })
      .map((m) => m.id);
    if (unreadIds.length > 0) {
      markChatMessagesAsRead({
        project_id: projectId,
        chat_id: thread.id,
        message_ids: unreadIds,
        // 楽観更新: 自分の seen_at を即時に立て、同一 ID の再送を抑止する
        reader_user_id: currentUserId,
      });
    }
  }, [messages, currentUserId, projectId, thread.id, markChatMessagesAsRead]);

  // ダウンロード既読の API 送信（楽観更新つき: mutation 側でキャッシュの
  // downloaded_users に自分を即時追加 → API 送信。オファー DM と同等の挙動）。
  // 一括ダウンロード時の連続呼び出しは配列 1 回の PATCH にまとめる。
  const enqueueMarkDownloaded = useBatchedCall<string>((fileIds) => {
    if (!projectId || !currentUserId) return;
    const self = thread.members.find((m) => m.id === currentUserId);
    markChatFilesAsDownloaded({
      project_id: projectId,
      chat_id: thread.id,
      downloaded_file_ids: fileIds,
      downloaded_user: self
        ? {
            id: self.id,
            display_name: self.display_name,
            avatar: self.avatar,
            downloaded_at: new Date().toISOString(),
          }
        : undefined,
    });
  });

  // ファイルダウンロード記録
  const handleFileDownload = useCallback(
    (messageId: string, fileId: string) => {
      if (!projectId || !currentUserId) return;

      // 既に自分がダウンロード済みなら何もしない（冪等）
      const msg = messages.find((m) => m.id === messageId);
      const targetFile = msg?.files.find((f) => f.id === fileId);
      if (targetFile?.downloaded_users?.some((u) => u.id === currentUserId)) {
        return;
      }

      enqueueMarkDownloaded(fileId);
    },
    [projectId, currentUserId, messages, enqueueMarkDownloaded],
  );

  const handleSend = async (text: string) => {
    const body = (text ?? "").trim();
    const hasPendingFiles = allSelectedFiles.length > 0;
    if ((!body && !hasPendingFiles) || !projectId) return;
    try {
      const fileKeys = hasPendingFiles ? await uploadPendingFiles() : [];
      // アップロード失敗ダイアログでキャンセル: 送信を中断（本文は復元される）
      if (fileKeys === null) return false;
      // 本文なしで全ファイルのアップロードに失敗した場合は送信しない。
      if (!body && fileKeys.length === 0) return;
      const doSend = () => sendChatMessage({
        project_id: projectId,
        chat_id: thread.id,
        message: body,
        file_keys: fileKeys.length > 0 ? fileKeys : undefined,
      }).unwrap();
      // 添付をアップロードした場合、送信が終わるまでは離脱を引き止める
      if (fileKeys.length > 0) {
        await withPostProcess(doSend, "メッセージを送信中");
      } else {
        await doSend();
      }
      refetch();
      setTimeout(scrollToBottom, 50);
    } catch (error) {
      console.warn("[ChatMessenger] sendChatMessage failed", error);
    }
  };

  // WS: 自分以外の参加者による作成 / 既読 / 削除を受信したら再取得して反映する。
  // （送信者自身は broadcast の exclude 対象なので二重取得にならない）
  const handleWsEvent = useCallback(() => {
    refetch();
  }, [refetch]);
  useMessengerSubscribe("chat", thread.id, handleWsEvent);
  // 再接続時（タブ復帰・瞬断復旧）は取りこぼし対策で一覧を再取得する。
  useMessengerReconnect(() => {
    refetch();
  });

  return (
    <Container ref={containerRef}>
      <DropZoneOverlay
        enabled={!isLoading}
        onDrop={handleFilesSelected}
        overlayText="ファイルまたはフォルダをドロップしてアップロード"
        dragActiveText="ここにドロップ"
        enableFolderDrop={true}
        onDropWithPath={handleFolderDrop}
      >
        <MessageListScroll
          ref={scrollRef}
          $bottomPadding={messengerBottomPadding}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          {isLoading ? (
            <SvgLoadingAnimation />
          ) : messages.length === 0 ? (
            <EmptyState>メッセージを送ってみましょう</EmptyState>
          ) : (
            messages.map((m) => (
              <MessageItem
                key={m.id}
                message={chatMessageToMessageData(m)}
                isOurs={m.author?.id === currentUserId}
                showAvatar
                currentUserId={currentUserId}
                onFileDownload={handleFileDownload}
              />
            ))
          )}
        </MessageListScroll>
      </DropZoneOverlay>

      {/* アップロード進捗モーダル */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            const stillUploading = uploadingFiles.some(
              (f) => f.status === "uploading" || f.status === "pending",
            );
            if (!stillUploading) clearCompleted();
          }}
          size="default"
        >
          <UploadModalTitle>ファイルアップロード</UploadModalTitle>
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={abortFile}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗の解決ダイアログ */}
      {uploadFailureDialog}

      {createPortal(
        <ChatActionPanel
          ref={commentFieldRef}
          $bottom={actionPanelBottom}
          $left={panelRect.left}
          $width={panelRect.width}
        >
          <CommentField
            onSend={handleSend}
            showPinIcon={false}
            showAttachIcon={true}
            placeholder="メッセージを送信"
            onFilesSelected={handleFilesSelected}
            onFilesDroppedWithPath={handleFolderDrop}
            selectedFiles={allSelectedFiles}
            onRemoveFile={handleRemoveFile}
            multipleFiles={true}
            enableChikuchiku={!isProducerSide}
            chikuchikuScene="chikuchiku_filter_chat"
            chikuchikuContext={{ project_id: projectId, chat_id: thread.id }}
            enableReplyAssistant={isProducerSide}
            replyAssistantScene="reply_assistant_chat"
            replyAssistantContext={{ project_id: projectId, chat_id: thread.id }}
          />
        </ChatActionPanel>,
        document.body,
      )}
    </Container>
  );
};

export default ChatMessenger;
